'use client';

import { LockSimpleIcon, ShoppingCartIcon, XIcon } from '@phosphor-icons/react';
import { BookOpen } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState, type MouseEvent } from 'react';

import {
  resolveVentaNocturnaCartNotice,
  type VentaNocturnaPhase,
} from '@/shared/campaigns/venta-nocturna';
import { ProductLineItem } from '../../../shared/ui/ProductLineItem';
import { useCart } from '../hooks/useCart';
import { formatCartPrice } from '../lib/cart-formatters';
import type { CartCurrency, CartItem } from '../types/cart';
import { CartPriceText } from './CartPriceText';
import { CartSheetLocalTotals, CartSheetSummary } from './CartSheetSummary';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

interface CartSheetItemProps {
  item: CartItem;
  href: string;
  onNavigate: (item: CartItem) => void;
  onRemove: () => void;
}

const CartSheetItem = ({ item, href, onNavigate, onRemove }: CartSheetItemProps) => {
  const [imageError, setImageError] = useState(false);
  const handleNavigate = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onNavigate(item);
  };

  return (
    <ProductLineItem
      href={href}
      image={
        <div className="h-18 w-18 rounded-adipa-md bg-adipa-surface-muted relative shrink-0 overflow-hidden">
          {item.image_url && !imageError ? (
            <Image
              alt={item.title}
              className="object-cover"
              fill
              sizes="72px"
              src={item.image_url}
              unoptimized
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="bg-adipa-surface-lavender absolute inset-0 flex items-center justify-center">
              <BookOpen aria-hidden="true" className="text-adipa-primary h-6 w-6 opacity-60" />
            </div>
          )}
        </div>
      }
      note={
        item.seats_left !== undefined && item.seats_left <= 5 ? (
          <p className="text-adipa-cart-price text-adipa-urgency font-semibold leading-4">
            Solo quedan {item.seats_left} cupos
          </p>
        ) : undefined
      }
      originalPrice={
        item.original_price && item.original_price > item.price
          ? formatCartPrice(item.original_price, item.currency)
          : undefined
      }
      price={<CartPriceText amount={item.price} currency={item.currency} />}
      subtitle={item.instructor_name || undefined}
      title={item.title}
      onNavigate={handleNavigate}
      onRemove={onRemove}
    />
  );
};

export interface CartSheetWidgetProps {
  /** Destination of the "Ir al checkout" CTA. */
  checkoutUrl?: string;
  /** Destination of the empty-state "Ver cursos" CTA. */
  emptyStateUrl?: string;
  /**
   * Optional informational banner rendered above the totals (e.g. campaign
   * notices). When provided, it overrides the built-in Venta Nocturna notice,
   * so the host can still fully control the copy if needed.
   */
  notice?: string;
  /** Optional destination that makes the notice banner clickable. */
  noticeHref?: string;
  /**
   * Fase de la campaña Venta Nocturna. Con `presale` el sheet muestra el aviso
   * a quien ya guardó su carrito (resuelto internamente desde `savedIds`).
   */
  vnPhase?: VentaNocturnaPhase;
  /**
   * IDs del carrito guardado del usuario para la Venta Nocturna. Los provee el
   * host desde su backend (legacy y front nuevo usan orígenes distintos).
   */
  savedIds?: readonly string[];
  /** Optional override of the default course detail URL builder. */
  resolveCourseUrl?: (item: CartItem) => string;
  /** Notified when the user clicks "Ir al checkout". */
  onCheckoutClick?: (items: CartItem[]) => void;
  /** Notified when the user clicks an item title to navigate to the course. */
  onItemNavigate?: (item: CartItem) => void;
  /**
   * Notified when the user removes an item. The host is responsible for
   * persisting the removal on its own backend (the store is updated locally
   * for instant feedback regardless).
   */
  onItemRemove?: (item: CartItem) => void;
}

const DEFAULT_CHECKOUT_URL = '/checkout';
const DEFAULT_EMPTY_STATE_URL = '/cursos';

const NOTICE_CLASS =
  'rounded-adipa-lg border-adipa-border bg-adipa-surface-lavender text-adipa-text-primary mb-3 block border p-3 text-xs font-medium leading-relaxed';

interface CartSheetNoticeProps {
  notice: string;
  noticeHref?: string;
  onNavigate: () => void;
}

const CartSheetNotice = ({ notice, noticeHref, onNavigate }: CartSheetNoticeProps) => {
  if (!noticeHref) return <p className={NOTICE_CLASS}>{notice}</p>;

  return (
    <a
      className={cx(
        NOTICE_CLASS,
        'duration-adipa-fast hover:border-adipa-border-strong transition-colors',
      )}
      href={noticeHref}
      onClick={onNavigate}
    >
      {notice}
    </a>
  );
};

export function CartSheetWidget({
  checkoutUrl = DEFAULT_CHECKOUT_URL,
  emptyStateUrl = DEFAULT_EMPTY_STATE_URL,
  notice,
  noticeHref,
  vnPhase,
  savedIds,
  resolveCourseUrl,
  onCheckoutClick,
  onItemNavigate,
  onItemRemove,
}: CartSheetWidgetProps) {
  const items = useCart((s) => s.items);
  const summary = useCart((s) => s.summary);
  const isOpen = useCart((s) => s.isOpen);
  const setIsOpen = useCart((s) => s.setIsOpen);
  const remove = useCart((s) => s.remove);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = isOpen ? 'hidden' : previousOverflow;
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const currency: CartCurrency = items[0]?.currency ?? 'CLP';

  // `notice` explícito = override total del host; si no, resolvemos el aviso de
  // Venta Nocturna internamente (solo se muestra a quien ya guardó su carrito).
  const vnNotice = resolveVentaNocturnaCartNotice(
    vnPhase ?? 'off',
    savedIds,
    items.map((i) => i.id),
  );
  const effectiveNotice = notice ?? vnNotice?.notice;
  const effectiveNoticeHref = notice ? noticeHref : vnNotice?.noticeHref;

  const resolveItemHref = (item: CartItem) =>
    resolveCourseUrl ? resolveCourseUrl(item) : `/cursos/${item.slug}`;

  const handleItemNavigate = (item: CartItem) => {
    setIsOpen(false);
    if (onItemNavigate) {
      onItemNavigate(item);
      return;
    }
    if (typeof window !== 'undefined') {
      window.location.href = resolveItemHref(item);
    }
  };

  const handleItemRemove = (item: CartItem) => {
    // Quita del store local de inmediato (feedback visual) y notifica al host
    // para que persista la baja en su propio backend.
    remove(item.id);
    onItemRemove?.(item);
  };

  const handleCheckoutClick = (event: MouseEvent<HTMLAnchorElement>) => {
    setIsOpen(false);
    if (onCheckoutClick) {
      event.preventDefault();
      onCheckoutClick(items);
    }
  };

  return (
    <>
      <button
        aria-label="Cerrar carrito"
        className={cx(
          'global-header-mobile-backdrop bg-adipa-text-primary/25 backdrop-blur-xs fixed inset-0 ease-in-out',
          mounted && 'duration-adipa-base transition-opacity',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        inert={!isOpen}
        type="button"
        onClick={() => setIsOpen(false)}
      />

      <aside
        aria-label="Carrito de compras"
        inert={!isOpen}
        className={cx(
          'global-header-mobile-drawer border-adipa-border bg-adipa-surface shadow-adipa-console-panel fixed right-0 top-0 flex h-full w-full max-w-none flex-col border-l sm:max-w-sm',
          mounted && 'duration-adipa-base transition-transform ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className="border-adipa-border flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2.5">
            <ShoppingCartIcon className="text-adipa-primary" size={20} weight="bold" />
            <h2 className="text-adipa-text-primary text-sm font-semibold">
              Tu carrito
              {items.length > 0 ? (
                <span className="text-adipa-text-secondary ml-1 font-medium">({items.length})</span>
              ) : null}
            </h2>
          </div>
          <button
            aria-label="Cerrar carrito"
            className={cx(
              'rounded-adipa-lg text-adipa-text-secondary inline-flex h-10 w-10 items-center justify-center',
              'duration-adipa-base hover:bg-adipa-surface-muted hover:text-adipa-primary transition-colors ease-in-out',
              'focus-visible:outline-adipa-primary focus-visible:outline-2 focus-visible:outline-offset-2',
            )}
            type="button"
            onClick={() => setIsOpen(false)}
          >
            <XIcon size={18} weight="bold" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
            <div className="rounded-adipa-full bg-adipa-surface-muted mb-4 flex h-16 w-16 items-center justify-center">
              <ShoppingCartIcon className="text-adipa-text-secondary" size={28} weight="regular" />
            </div>
            <h3 className="text-adipa-text-primary mb-1 text-base font-semibold">
              Tu carrito está vacío
            </h3>
            <p className="text-adipa-text-secondary mb-6 text-sm">
              Explora el catálogo y agrega tu próximo curso.
            </p>
            <a
              className="rounded-adipa-lg bg-adipa-primary text-adipa-primary-foreground duration-adipa-fast hover:bg-adipa-primary-hover inline-flex items-center px-5 py-2.5 text-sm font-semibold transition-colors"
              href={emptyStateUrl}
              onClick={() => setIsOpen(false)}
            >
              Ver cursos
            </a>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-2.5 overflow-y-auto px-4 py-4">
              {items.map((item) => (
                <CartSheetItem
                  href={resolveItemHref(item)}
                  item={item}
                  key={item.id}
                  onNavigate={handleItemNavigate}
                  onRemove={() => handleItemRemove(item)}
                />
              ))}
            </div>

            <div className="border-adipa-border bg-adipa-surface-muted border-t px-4 pb-5 pt-4">
              {effectiveNotice ? (
                <CartSheetNotice
                  notice={effectiveNotice}
                  noticeHref={effectiveNoticeHref}
                  onNavigate={() => setIsOpen(false)}
                />
              ) : null}
              {summary ? (
                <CartSheetSummary summary={summary} />
              ) : (
                <CartSheetLocalTotals currency={currency} items={items} />
              )}

              <a
                className={cx(
                  'rounded-adipa-lg bg-adipa-primary text-adipa-primary-foreground shadow-adipa-card block w-full py-3.5 text-center text-sm font-bold',
                  'duration-adipa-base hover:bg-adipa-primary-hover transition-colors ease-out active:scale-[0.98]',
                )}
                href={checkoutUrl}
                onClick={handleCheckoutClick}
              >
                Ir al checkout
              </a>

              <p className="text-adipa-text-secondary mt-3 flex items-center justify-center gap-1.5 text-xs">
                <LockSimpleIcon size={12} weight="bold" />
                Pago seguro y protegido
              </p>
            </div>
          </>
        )}
      </aside>
    </>
  );
}

export { CartSheetWidget as CartSheet };
